/**
 * Раскладка поля: штаб в центре (UX: «база в центре, зомби со всех сторон», docs/context/ux-decisions-2026-10-24.md),
 * шесть платформ вокруг, зомби появляются на краях поля. Координаты — мир, фиксированная точка
 * (`x` поперёк, центр 0; `y` от 0 до `field.depth`).
 */
import { DEFAULT_FIELD, FIXED_ONE, type FieldGeometry, type Vec2 } from "@newfungame/contracts";

/** Радиус штаба: зомби останавливается на `HQ_RADIUS + радиус зомби` от центра и бьёт. */
export const HQ_RADIUS = FIXED_ONE;
/** Радиус обычного зомби и босса. */
export const ZOMBIE_RADIUS = 300;
export const BOSS_RADIUS = 600;

/** Смещения платформ 0…5 от центра штаба. */
const PLATFORM_OFFSETS: readonly Vec2[] = [
  { x: -1600, y: 1400 },
  { x: 1600, y: 1400 },
  { x: -1600, y: -1400 },
  { x: 1600, y: -1400 },
  { x: 0, y: 2600 },
  { x: 0, y: -2600 },
];

/**
 * Бета: сетка 4×4 вокруг штаба (16 слотов; центр свободен для штаба). Клетки — по 1700 единиц, слоты идут по рядам
 * сверху вниз.
 */
const GRID_STEPS = [-2550, -850, 850, 2550] as const;
const GRID_OFFSETS: readonly Vec2[] = GRID_STEPS.flatMap((y) => GRID_STEPS.map((x) => ({ x, y })));

export interface Layout {
  field: FieldGeometry;
  hq: Vec2;
  platforms: readonly Vec2[];
}

export function createLayout(field: FieldGeometry = DEFAULT_FIELD, grid = false): Layout {
  const hq = { x: 0, y: Math.trunc(field.depth / 2) };
  const offsets = grid ? GRID_OFFSETS : PLATFORM_OFFSETS;
  return { field, hq, platforms: offsets.map((o) => ({ x: hq.x + o.x, y: hq.y + o.y })) };
}

/** Периметр поля, единиц. */
export function perimeter(field: FieldGeometry): number {
  return 2 * (field.width + field.depth);
}

/** Точка на краю поля по смещению `r ∈ [0, perimeter)` (обход: низ → правый край → верх → левый край). */
export function perimeterPoint(field: FieldGeometry, r: number): Vec2 {
  const half = Math.trunc(field.width / 2);
  const w = field.width;
  const d = field.depth;
  if (r < w) return { x: -half + r, y: 0 };
  r -= w;
  if (r < d) return { x: half, y: r };
  r -= d;
  if (r < w) return { x: half - r, y: d };
  r -= w;
  return { x: -half, y: d - r };
}

/** Радиус платформы героя: зомби останавливается на `PLATFORM_RADIUS + радиус зомби` от её центра (бой атаки). */
export const PLATFORM_RADIUS = 600;

/**
 * Бой атаки (режим «Карта»): база-цель в центре поля, 16 платформ атакующего по краю — квадратное кольцо с полусторон
 * 3600 (по 5 точек на сторону, углы общие). Порядок слотов: углы, середины сторон, остальное — первые слоты расставлены
 * по периметру равномерно.
 */
const RING_HALF = 3600;
const RING_SLOTS: readonly Vec2[] = [
  { x: -RING_HALF, y: -RING_HALF },
  { x: RING_HALF, y: -RING_HALF },
  { x: -RING_HALF, y: RING_HALF },
  { x: RING_HALF, y: RING_HALF },
  { x: 0, y: -RING_HALF },
  { x: 0, y: RING_HALF },
  { x: -RING_HALF, y: 0 },
  { x: RING_HALF, y: 0 },
  { x: -RING_HALF / 2, y: -RING_HALF },
  { x: RING_HALF / 2, y: -RING_HALF },
  { x: -RING_HALF / 2, y: RING_HALF },
  { x: RING_HALF / 2, y: RING_HALF },
  { x: -RING_HALF, y: -RING_HALF / 2 },
  { x: RING_HALF, y: -RING_HALF / 2 },
  { x: -RING_HALF, y: RING_HALF / 2 },
  { x: RING_HALF, y: RING_HALF / 2 },
];

export interface AttackLayout extends Layout {
  /** Платформы героев-защитников вокруг базы (сетка 4×4 беты), по слотам. */
  defenders: readonly Vec2[];
}

export function createAttackLayout(field: FieldGeometry = DEFAULT_FIELD): AttackLayout {
  const hq = { x: 0, y: Math.trunc(field.depth / 2) };
  return {
    field,
    hq,
    platforms: RING_SLOTS.map((o) => ({ x: hq.x + o.x, y: hq.y + o.y })),
    defenders: GRID_OFFSETS.map((o) => ({ x: hq.x + o.x, y: hq.y + o.y })),
  };
}
